import { useEffect, useMemo, useRef, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { useSelector } from "react-redux"
import { toast } from "react-toastify"
import { selectCurrentBranch } from "../../../../core/data/redux/slice/branchesSlice"
import { selectCurrentSession } from "../../../../core/data/redux/slice/sessionsSlice"
import { selectSystemsForCurrentBranch } from "../../../../core/data/redux/slice/systemsSlice"
import { getAllClasses, type Class as ClassType } from "../../../../service/classes"
import {
  getStatusOptions,
  getStudentStanding,
  getStudentStandingByGr,
  changeStudentStatus,
  listStudentsByStatus,
  resolveByGrNumbers,
  bulkChangeStatus,
  type StudentStatusOption,
  type StudentStanding,
  type RegisterStudent,
  type ResolvedStudent,
  type SectionBreakdownItem,
} from "../../../../service/student-status"
import { issueTransferCertificate } from "../../../../service/transfer-certificate"

const fmt = (d?: string | null) => (d ? new Date(d).toLocaleDateString("en-GB") : "—")

// Filter buttons across the top of the register. "all" (default) is every
// student regardless of standing, so the office can browse and pick one
// without already knowing the GR — not just the ones currently off Active.
const REGISTER_FILTERS: { value: string; label: string }[] = [
  { value: "all", label: "All" },
  { value: "admitted", label: "Active" },
  { value: "struck_off", label: "Struck off" },
  { value: "transfer_certificate", label: "TC issued" },
  { value: "left_without_info", label: "Left without information" },
]

// The register is the one screen — everything else (viewing or changing a
// single student, changing several) opens as a modal ON TOP of it, rather
// than as its own card further down the page. Opening one used to mean a
// second, disconnected search box at the top of the page whose result
// appeared above a register you'd already scrolled past — confusing, and it
// didn't match how "View" on a register row jumped you back up there either.
// A modal opens wherever you triggered it from, every time.
const StudentStatusPage = () => {
  const [searchParams] = useSearchParams()
  const modalTriggerRef = useRef<HTMLButtonElement>(null)
  const currentBranch = useSelector(selectCurrentBranch)
  const currentSession = useSelector(selectCurrentSession)
  const systems = useSelector(selectSystemsForCurrentBranch)

  const [options, setOptions] = useState<StudentStatusOption[]>([])
  const [gr, setGr] = useState("")
  const [looking, setLooking] = useState(false)
  const [standing, setStanding] = useState<StudentStanding | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const [toStatus, setToStatus] = useState("")
  const [reason, setReason] = useState("")
  const [effectiveDate, setEffectiveDate] = useState("")

  // System, Class and Section categorization
  const [selectedSystem, setSelectedSystem] = useState<string>("")
  const [classes, setClasses] = useState<ClassType[]>([])
  const [selectedClass, setSelectedClass] = useState<string>("")
  const [selectedSection, setSelectedSection] = useState<string>("all")
  const [sectionBreakdown, setSectionBreakdown] = useState<SectionBreakdownItem[]>([])

  // Register — filterable by the buttons below the header, scoped to
  // whichever campus is selected at the top of the screen (same as every
  // other list in the app), so the office can find a student again without
  // already knowing the GR.
  const [rows, setRows] = useState<RegisterStudent[]>([])
  const [rowsLoading, setRowsLoading] = useState(false)
  const [registerSearch, setRegisterSearch] = useState("")
  const [registerFilter, setRegisterFilter] = useState("all")
  const [registerPage, setRegisterPage] = useState(1)
  const [registerTotalPages, setRegisterTotalPages] = useState(1)
  const [registerTotal, setRegisterTotal] = useState(0)

  // Bulk mode — built either by ticking rows in the register, or by pasting a
  // list of GR numbers straight off an export. Both end up in the same map,
  // so one Apply covers whichever way the list was built. `bulkOpen` is its
  // own toggle, separate from `selected.size` — a bulk batch usually STARTS
  // from zero selected (pasting a list of GRs nobody has ticked yet), so the
  // entry point can't be hidden behind already having something selected.
  const [selected, setSelected] = useState<Map<string, ResolvedStudent>>(new Map())
  const [bulkOpen, setBulkOpen] = useState(false)
  const [grPasteText, setGrPasteText] = useState("")
  const [resolvingGrs, setResolvingGrs] = useState(false)
  const [bulkToStatus, setBulkToStatus] = useState("")
  const [bulkReason, setBulkReason] = useState("")
  const [bulkEffectiveDate, setBulkEffectiveDate] = useState(new Date().toISOString().slice(0, 10))
  const [applyingBulk, setApplyingBulk] = useState(false)

  useEffect(() => {
    getStatusOptions().then((r) => setOptions(r?.data || []))
  }, [])

  // Load available classes scoped to the current campus, session, and optional system
  useEffect(() => {
    if (!currentBranch?._id) {
      setClasses([])
      return
    }
    getAllClasses({
      limit: 200,
      system: selectedSystem || undefined,
      session: currentSession?._id,
      branch: currentBranch._id,
      status: "active",
      sortBy: "order",
      sortOrder: "asc",
    })
      .then((r: any) => setClasses(r?.classes || []))
      .catch(() => setClasses([]))
  }, [currentBranch?._id, currentSession?._id, selectedSystem])

  const totalSectionCount = useMemo(() => {
    return sectionBreakdown.reduce((sum, s) => sum + (s.count || 0), 0)
  }, [sectionBreakdown])

  const loadRegister = async (opts?: {
    search?: string
    status?: string
    systemId?: string
    classId?: string
    sectionId?: string
    page?: number
  }) => {
    setRowsLoading(true)
    try {
      const page = opts?.page ?? registerPage
      const activeSystem = opts?.systemId !== undefined ? opts.systemId : selectedSystem
      const activeClass = opts?.classId !== undefined ? opts.classId : selectedClass
      const activeSection = opts?.sectionId !== undefined ? opts.sectionId : selectedSection
      const r = await listStudentsByStatus({
        search: opts?.search ?? registerSearch,
        status: opts?.status ?? registerFilter,
        branchId: currentBranch?._id,
        systemId: activeSystem || undefined,
        classId: activeClass || undefined,
        sectionId: activeSection !== "all" ? activeSection : undefined,
        sessionId: currentSession?._id,
        page,
        limit: 20,
      })
      setRows(r.data || [])
      setSectionBreakdown(r.sectionBreakdown || [])
      setRegisterPage(r.pagination?.page || page)
      setRegisterTotalPages(r.pagination?.totalPages || 1)
      setRegisterTotal(r.pagination?.total || 0)
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Could not load the register")
    } finally {
      setRowsLoading(false)
    }
  }

  useEffect(() => {
    setSelectedSystem("")
    setSelectedClass("")
    setSelectedSection("all")
    loadRegister({ systemId: "", classId: "", sectionId: "all", page: 1 })
    // Reload whenever the campus selected at the top of the screen changes —
    // this register is scoped to it same as every other list in the app.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBranch?._id])

  const selectFilter = (value: string) => {
    setRegisterFilter(value)
    loadRegister({ status: value, page: 1 })
  }

  const handleSystemChange = (newSystemId: string) => {
    setSelectedSystem(newSystemId)
    setSelectedClass("")
    setSelectedSection("all")
    loadRegister({ systemId: newSystemId, classId: "", sectionId: "all", page: 1 })
  }

  const handleClassChange = (newClassId: string) => {
    setSelectedClass(newClassId)
    setSelectedSection("all")
    loadRegister({ classId: newClassId, sectionId: "all", page: 1 })
  }

  const handleSectionChange = (newSectionId: string) => {
    setSelectedSection(newSectionId)
    loadRegister({ sectionId: newSectionId, page: 1 })
  }

  const searchRegister = () => loadRegister({ page: 1 })

  const goToPage = (page: number) => loadRegister({ page })

  const toggleRowSelection = (r: RegisterStudent) => {
    setSelected((prev) => {
      const next = new Map(prev)
      if (next.has(r.studentId)) {
        next.delete(r.studentId)
      } else {
        next.set(r.studentId, {
          studentId: r.studentId,
          grNumber: r.grNumber,
          name: r.name,
          status: r.status,
          statusLabel: r.statusLabel,
          campusName: r.campusName,
        })
      }
      return next
    })
    setBulkOpen(true)
  }

  const removeFromSelection = (studentId: string) => {
    setSelected((prev) => {
      const next = new Map(prev)
      next.delete(studentId)
      return next
    })
  }

  const clearSelection = () => {
    setSelected(new Map())
    setGrPasteText("")
    setBulkReason("")
    setBulkOpen(false)
  }

  // Splits on commas, spaces or newlines — however someone actually pastes a
  // column out of Excel.
  const addFromGrPaste = async () => {
    const grNumbers = Array.from(new Set(
      grPasteText.split(/[\s,]+/).map((g) => g.trim()).filter(Boolean)
    ))
    if (!grNumbers.length) return
    setResolvingGrs(true)
    try {
      const r = await resolveByGrNumbers(grNumbers)
      setSelected((prev) => {
        const next = new Map(prev)
        r.found.forEach((s) => next.set(s.studentId, s))
        return next
      })
      if (r.notFound.length) {
        toast.warning(`Not found: ${r.notFound.join(", ")}`)
      }
      setGrPasteText("")
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Could not look those up")
    } finally {
      setResolvingGrs(false)
    }
  }

  const bulkReasonRequired = bulkToStatus !== "admitted"
  const canApplyBulk =
    !applyingBulk &&
    selected.size > 0 &&
    !!bulkToStatus &&
    (!bulkReasonRequired || bulkReason.trim().length > 0)

  const applyBulk = async () => {
    if (!canApplyBulk) return
    setApplyingBulk(true)
    try {
      const r = await bulkChangeStatus({
        studentIds: Array.from(selected.keys()),
        toStatus: bulkToStatus,
        reason: bulkReason.trim(),
        effectiveDate: bulkEffectiveDate || undefined,
      })
      if (r.failed.length) {
        toast.warning(`${r.message} — ${r.failed.map((f) => f.message).join("; ")}`)
      } else {
        toast.success(r.message)
      }
      clearSelection()
      loadRegister()
      // The modal may be open on one of the students just changed — refresh
      // it rather than leave it quietly stale.
      if (standing) {
        getStudentStanding(standing.studentId).then((fresh) => openStanding(fresh.data))
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Could not apply the change")
    } finally {
      setApplyingBulk(false)
    }
  }

  // The one place a student's detail opens from, regardless of whether the
  // trigger was the quick-jump box, a register row's View button, a deep
  // link, or a post-save refresh — always the modal, never a panel elsewhere
  // on the page.
  const openStanding = async (data: StudentStanding) => {
    setStanding(data)
    setToStatus(data.status)
    setReason("")
    setEffectiveDate(new Date().toISOString().slice(0, 10))
    modalTriggerRef.current?.click()
  }

  const lookUp = async (grOverride?: string) => {
    const value = (grOverride ?? gr).trim()
    if (!value) return
    // This box opens one student. More than one GR-looking token pasted in
    // here means someone meant Bulk select, not a single lookup — sending
    // the whole string as one GR would just fail with a confusing "not
    // found", so redirect them instead of trying.
    if (/[\s,]/.test(value)) {
      toast.warning('That looks like more than one GR number — use "Bulk select" below for multiple students.')
      setBulkOpen(true)
      return
    }
    setLooking(true)
    try {
      const r = await getStudentStandingByGr(value)
      await openStanding(r.data)
      setGr("")
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "No student found with that GR number")
    } finally {
      setLooking(false)
    }
  }

  // Deep link from the Student List row action, or from a register row.
  useEffect(() => {
    const studentId = searchParams.get("studentId")
    const grParam = searchParams.get("gr")
    if (studentId) {
      setLoading(true)
      getStudentStanding(studentId)
        .then((r) => openStanding(r.data))
        .catch((e: any) => toast.error(e?.response?.data?.message || "Could not load that student"))
        .finally(() => setLoading(false))
    } else if (grParam) {
      lookUp(grParam)
    }
    // Only meant to run once, off whatever the URL carried on arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const closePanel = () => setStanding(null)

  // TC issued (2026-09-30, back in this dropdown, second decision the same
  // day) stays a direct dropdown pick — same simple Reason/Effective date
  // form as any other status, one button, no page switch. But saving it
  // still goes through the real Transfer Certificate service underneath
  // (serial number, register entry), not a plain status flip -- that's the
  // whole reason "TC issued" ever needed removing in the first place (a
  // student once showed "TC Issued" with no certificate behind it). Always
  // fee-waived: this entry point is specifically for a student who already
  // left before this system tracked TCs, so there's no live fee to check --
  // issuing a TC for a currently-enrolled student still goes through the
  // full Transfer Certificate page, fee checked as normal there.
  const selectedOption = options.find((o) => o.value === toStatus)
  const requiresIssuance = !!selectedOption?.requiresIssuance

  const reasonRequired = toStatus !== "admitted"
  const unchanged = toStatus === standing?.status
  const canSave =
    !saving && !unchanged && !!toStatus && (!reasonRequired || reason.trim().length > 0)

  const save = async () => {
    if (!standing) return
    setSaving(true)
    try {
      if (requiresIssuance) {
        const res = await issueTransferCertificate({
          studentId: standing.studentId,
          reasonForLeaving: reason.trim(),
          dateOfLeaving: effectiveDate || undefined,
          issueDate: effectiveDate || undefined,
          feeWaived: true,
        })
        toast.success(res.message || "Certificate issued")
      } else {
        const res = await changeStudentStatus(standing.studentId, {
          toStatus,
          reason: reason.trim(),
          effectiveDate: effectiveDate || undefined,
        })
        toast.success(res.message || "Status updated")
      }
      const fresh = await getStudentStanding(standing.studentId)
      await openStanding(fresh.data)
      loadRegister()
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Could not change the status")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="mb-3">
          <h3 className="page-title mb-1">Student Status</h3>
          <p className="text-muted mb-0">
            Open a student from the register below to change their standing —
            struck off, TC issued, left without information.
          </p>
        </div>

        {/* Opens the modal below once its data has loaded, wherever it was
            triggered from — the trigger itself stays invisible. */}
        <button
          ref={modalTriggerRef}
          type="button"
          className="d-none"
          data-bs-toggle="modal"
          data-bs-target="#student_status_modal"
        />

        {/* Register — the one screen for this page. */}
        <div className="card">
          <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-3">
            <h6 className="mb-0">
              Register
              {currentBranch?.name && <span className="text-muted fw-normal"> — {currentBranch.name}</span>}
            </h6>
            <div className="d-flex gap-3 flex-wrap align-items-center">
              <div className="d-flex gap-2 align-items-center">
                <input
                  className="form-control form-control-sm"
                  style={{ width: 140 }}
                  placeholder="Open by GR…"
                  value={gr}
                  onChange={(e) => setGr(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && lookUp()}
                />
                <button
                  className="btn btn-sm btn-primary"
                  onClick={() => lookUp()}
                  disabled={looking || !gr.trim()}
                >
                  {looking ? "…" : "Open"}
                </button>
              </div>
              <div className="d-flex gap-2 align-items-center">
                <input
                  className="form-control form-control-sm"
                  style={{ width: 180 }}
                  placeholder="Search name or GR"
                  value={registerSearch}
                  onChange={(e) => setRegisterSearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && searchRegister()}
                />
                <button className="btn btn-sm btn-outline-secondary" onClick={searchRegister}>Search</button>
              </div>
              <button
                type="button"
                className={`btn btn-sm ${bulkOpen ? "btn-secondary" : "btn-outline-secondary"}`}
                onClick={() => (bulkOpen ? clearSelection() : setBulkOpen(true))}
              >
                {bulkOpen ? "Exit bulk select" : "Bulk select"}
              </button>
            </div>
          </div>

          <div className="card-body py-2 border-bottom d-flex flex-wrap align-items-center justify-content-between gap-3">
            <div className="d-flex gap-2 flex-wrap">
              {REGISTER_FILTERS.map((f) => (
                <button
                  key={f.value}
                  className={`btn btn-sm ${registerFilter === f.value ? "btn-primary" : "btn-outline-secondary"}`}
                  onClick={() => selectFilter(f.value)}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div className="d-flex align-items-center gap-3 flex-wrap">
              {systems.length > 1 && (
                <div className="d-flex align-items-center gap-2">
                  <label className="small text-muted fw-bold text-uppercase text-nowrap mb-0">System:</label>
                  <select
                    className="form-select form-select-sm"
                    style={{ minWidth: 140 }}
                    value={selectedSystem}
                    onChange={(e) => handleSystemChange(e.target.value)}
                  >
                    <option value="">All Systems</option>
                    {systems.map((s) => (
                      <option key={s._id} value={s._id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="d-flex align-items-center gap-2">
                <label className="small text-muted fw-bold text-uppercase text-nowrap mb-0">Class:</label>
                <select
                  className="form-select form-select-sm"
                  style={{ minWidth: 160 }}
                  value={selectedClass}
                  onChange={(e) => handleClassChange(e.target.value)}
                >
                  <option value="">All Classes</option>
                  {classes.map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Section Filter Pills */}
          {selectedClass && sectionBreakdown.length > 0 && (
            <div className="card-header border-top-0 border-bottom bg-light-subtle py-2 px-4">
              <div className="d-flex align-items-center flex-wrap gap-2">
                <span className="text-muted small fw-bold text-uppercase me-2">Sections:</span>
                <button
                  type="button"
                  className={`btn btn-xs rounded-pill px-3 py-1 fw-medium ${selectedSection === "all" ? "btn-dark" : "btn-light border text-dark"}`}
                  onClick={() => handleSectionChange("all")}
                >
                  All Sections <span className="badge bg-secondary-subtle text-secondary-emphasis rounded-pill ms-1">{totalSectionCount}</span>
                </button>
                {sectionBreakdown.map((sec) => (
                  <button
                    key={sec._id}
                    type="button"
                    className={`btn btn-xs rounded-pill px-3 py-1 fw-medium ${selectedSection === sec._id ? "btn-dark" : "btn-light border text-dark"}`}
                    onClick={() => handleSectionChange(sec._id)}
                  >
                    Section {sec.name} <span className="badge bg-secondary-subtle text-secondary-emphasis rounded-pill ms-1">{sec.count}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Bulk bar — opened either from "Bulk select" above (to start a
              batch from zero, typically by pasting GR numbers) or the moment
              a checkbox is ticked. Either way in, the same bar. */}
          {bulkOpen && (
            <div className="card-body py-2 border-bottom bg-light">
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                <strong className="small">
                  {selected.size > 0 ? `${selected.size} selected` : "Paste GR numbers, or tick rows below"}
                </strong>
                <button className="btn btn-sm btn-outline-secondary" onClick={clearSelection}>Clear</button>
              </div>

              <div className="d-flex gap-2 flex-wrap align-items-start mb-2">
                <textarea
                  className="form-control form-control-sm"
                  style={{ maxWidth: 420 }}
                  rows={2}
                  value={grPasteText}
                  onChange={(e) => setGrPasteText(e.target.value)}
                  placeholder={"e.g.\n135226\n135227, 135228"}
                />
                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={resolvingGrs || !grPasteText.trim()}
                  onClick={addFromGrPaste}
                >
                  {resolvingGrs ? "Looking up…" : "Add to selection"}
                </button>
              </div>

              <div className="d-flex flex-wrap gap-2 mb-2">
                {Array.from(selected.values()).map((s) => {
                  // GR search/paste works across every campus — the register
                  // only shows whichever one is currently selected, so a
                  // student from elsewhere needs flagging here or the change
                  // looks like it never happened once you go looking in the
                  // register.
                  const otherCampus = s.campusName && s.campusName !== currentBranch?.name
                  return (
                    <span
                      key={s.studentId}
                      className={`d-flex align-items-center gap-1 py-1 px-2 border rounded bg-white ${otherCampus ? "border-warning" : ""}`}
                    >
                      <span className="small">{s.name || s.grNumber} · GR {s.grNumber}</span>
                      <span className={`badge ${s.status === "admitted" ? "badge-soft-success" : "badge-soft-danger"}`}>
                        {s.statusLabel}
                      </span>
                      {otherCampus && (
                        <span
                          className="badge badge-soft-warning"
                          title={`This student is at ${s.campusName} — switch the campus selector above to see them in the register`}
                        >
                          {s.campusName}
                        </span>
                      )}
                      <button
                        type="button"
                        className="btn-close ms-1"
                        style={{ fontSize: 8 }}
                        onClick={() => removeFromSelection(s.studentId)}
                        aria-label="Remove"
                      />
                    </span>
                  )
                })}
              </div>

              <div className="row g-2 align-items-end">
                <div className="col-md-4">
                  <label className="form-label small mb-1">Change to</label>
                  <select
                    className="form-select form-select-sm"
                    value={bulkToStatus}
                    onChange={(e) => setBulkToStatus(e.target.value)}
                  >
                    <option value="">— Select —</option>
                    {/* TC issued is excluded here — it needs its own reason/date
                        per student (a real certificate, not a batch flip), so it's
                        single-student only, same as the modal above but for bulk. */}
                    {options.filter((o) => !o.requiresIssuance).map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label small mb-1">Effective date</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={bulkEffectiveDate}
                    onChange={(e) => setBulkEffectiveDate(e.target.value)}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label small mb-1">
                    Reason {bulkReasonRequired && <span className="text-danger">*</span>}
                  </label>
                  <input
                    className="form-control form-control-sm"
                    value={bulkReason}
                    onChange={(e) => setBulkReason(e.target.value)}
                    placeholder={bulkReasonRequired ? "Why are these coming off Active?" : "Optional"}
                  />
                </div>
                <div className="col-md-2 d-grid">
                  <button className="btn btn-sm btn-primary" disabled={!canApplyBulk} onClick={applyBulk}>
                    {applyingBulk ? "Applying…" : `Apply (${selected.size})`}
                  </button>
                </div>
              </div>
              {/* One shared reason/date applies to the whole batch — each
                  student still goes through the same admin-only-past-TC and
                  reason-required checks individually, so one refusal (e.g.
                  already TC'd) is reported, not silently absorbed. */}
            </div>
          )}

          <div className="card-body p-0">
            {rowsLoading ? (
              <div className="text-center text-muted py-4">Loading…</div>
            ) : rows.length === 0 ? (
              <div className="text-center text-muted py-4">No students match this filter.</div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: 32 }} />
                      <th>GR</th>
                      <th>Name</th>
                      <th>Campus</th>
                      <th>Class</th>
                      <th>Section</th>
                      <th>Status</th>
                      <th>Last change</th>
                      <th>Reason</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.studentId} className={selected.has(r.studentId) ? "table-active" : ""}>
                        <td>
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={selected.has(r.studentId)}
                            onChange={() => toggleRowSelection(r)}
                          />
                        </td>
                        <td>{r.grNumber}</td>
                        <td>{r.name}</td>
                        <td>{r.campusName || "—"}</td>
                        <td>{r.className || "—"}</td>
                        <td>{r.sectionName ? `Sec ${r.sectionName}` : "—"}</td>
                        <td>
                          <span className={`badge ${r.status === "admitted" ? "badge-soft-success" : "badge-soft-danger"}`}>
                            {r.statusLabel}
                          </span>
                        </td>
                        {/* One timeline, not two half-empty columns — whichever
                            happened more recently, a status change or a
                            leave/suspension being recorded, is what shows
                            here. */}
                        <td>{r.lastChange ? fmt(r.lastChange.date) : "—"}</td>
                        <td>
                          {!r.lastChange ? (
                            "—"
                          ) : r.lastChange.label ? (
                            <>
                              <strong>{r.lastChange.label}</strong>
                              {r.lastChange.reason ? ` — ${r.lastChange.reason}` : ""}
                            </>
                          ) : (
                            r.lastChange.reason || "—"
                          )}
                        </td>
                        <td className="text-nowrap">
                          <button className="btn btn-sm btn-outline-secondary" onClick={() => lookUp(r.grNumber)}>
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          {registerTotalPages > 1 && (
            <div className="card-footer d-flex align-items-center justify-content-between">
              <small className="text-muted">
                {registerTotal} student{registerTotal === 1 ? "" : "s"} — page {registerPage} of {registerTotalPages}
              </small>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={rowsLoading || registerPage <= 1}
                  onClick={() => goToPage(registerPage - 1)}
                >
                  Prev
                </button>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={rowsLoading || registerPage >= registerTotalPages}
                  onClick={() => goToPage(registerPage + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Single-student detail + status change — opens wherever it was
          triggered from, always as this modal. */}
      <div className="modal fade" id="student_status_modal" tabIndex={-1}>
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            {loading ? (
              <div className="modal-body text-center text-muted py-5">Loading…</div>
            ) : standing ? (
              <>
                <div className="modal-header">
                  <div>
                    <h6 className="mb-0">
                      {standing.name} — GR {standing.grNumber}
                    </h6>
                    <small className="text-muted">
                      {[standing.campusName, [standing.className, standing.sectionName].filter(Boolean).join(" ")]
                        .filter(Boolean)
                        .join(" · ")}
                    </small>
                  </div>
                  <button type="button" className="btn-close" data-bs-dismiss="modal" onClick={closePanel} />
                </div>
                <div className="modal-body">
                  <div className="alert alert-light border text-dark py-2 small">
                    Current status: <strong>{standing.statusLabel}</strong>
                    {standing.topLevel === "inactive" && " — this student is off the rolls"}
                  </div>

                  {/* The two grounds a struck-off/left-without-information call
                      actually gets made on, plus the number to call about it —
                      so the office isn't opening Fees and Attendance
                      separately for every name on a list. */}
                  <div className="row g-3 mb-1">
                    <div className="col-md-4">
                      <div className="text-muted small">Outstanding fees</div>
                      <div className={`fw-medium ${standing.feeSummary.outstanding > 0 ? "text-danger" : ""}`}>
                        {standing.feeSummary.outstanding > 0
                          ? `Rs. ${standing.feeSummary.outstanding.toLocaleString()} · ${standing.feeSummary.unpaidMonths} month(s) unpaid`
                          : "Nothing pending"}
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="text-muted small">Last attendance</div>
                      <div className="fw-medium">{fmt(standing.lastAttendanceDate)}</div>
                    </div>
                    <div className="col-md-4">
                      <div className="text-muted small">Guardian contact</div>
                      <div className="fw-medium">
                        {standing.guardianContact ? (
                          <a href={`tel:${standing.guardianContact}`}>{standing.guardianContact}</a>
                        ) : (
                          "—"
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">Change to</label>
                      <select
                        className="form-select"
                        value={toStatus}
                        onChange={(e) => setToStatus(e.target.value)}
                      >
                        {options.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">
                        {requiresIssuance ? "Date of leaving" : "Effective date"}
                      </label>
                      <input
                        type="date"
                        className="form-control"
                        value={effectiveDate}
                        onChange={(e) => setEffectiveDate(e.target.value)}
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">
                        Reason {reasonRequired && <span className="text-danger">*</span>}
                      </label>
                      <textarea
                        className="form-control"
                        rows={2}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder={
                          requiresIssuance
                            ? "e.g. Left in 2023, recording now for the register"
                            : reasonRequired
                              ? "Why is this student coming off Active?"
                              : "Optional"
                        }
                      />
                    </div>
                    {requiresIssuance && (
                      <div className="col-12">
                        <div className="alert alert-light border text-dark py-2 small mb-0">
                          This creates a real Transfer Certificate (serial number, TC
                          register entry) for a student who already left — no fee is
                          checked or collected here. For issuing a TC to a currently
                          enrolled student, use the Transfer Certificate page instead,
                          where fees are checked as normal.
                        </div>
                      </div>
                    )}
                  </div>

                  <h6 className="mt-4 mb-2">History</h6>
                  {standing.history?.length ? (
                    <div className="table-responsive">
                      <table className="table table-sm mb-0">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Change</th>
                            <th>Reason</th>
                            <th>By</th>
                          </tr>
                        </thead>
                        <tbody>
                          {standing.history.map((h) => (
                            <tr key={h._id}>
                              <td>{fmt(h.effectiveDate)}</td>
                              <td className="text-nowrap">
                                {h.fromStatus} → <strong>{h.toStatus}</strong>
                              </td>
                              <td>{h.reason || "—"}</td>
                              <td>
                                {h.changedBy
                                  ? `${h.changedBy.firstName || ""} ${h.changedBy.lastName || ""}`.trim()
                                  : h.source}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-muted small mb-0">
                      No changes recorded — this student has always been Active.
                    </p>
                  )}
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary me-2" data-bs-dismiss="modal" onClick={closePanel}>Cancel</button>
                  <button className="btn btn-primary" disabled={!canSave} onClick={save}>
                    {saving
                      ? "Saving…"
                      : unchanged
                        ? "No change"
                        : requiresIssuance
                          ? "Issue TC"
                          : "Change status"}
                  </button>
                </div>
              </>
            ) : (
              <div className="modal-body text-center text-muted py-5">No student selected.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default StudentStatusPage
